<template>
  <div class="FileUpload"
       :class="[dragHover && 'FileUpload--drag-hover']"
       @dragenter="dragHover = true"
       @dragleave="dragHover = false"
       @click.self="$refs.htmlInput.click()">
    <input ref="htmlInput" type="file" :multiple="multiple"
           class="FileUpload__htmlInput" :accept="accept" :required="htmlInputRequired"
           @change="onHtmlInputChange">
    <div class="FileUpload__required" />

    <slot v-if="state === STATES.EMPTY || multiple" name="dialog">
      <div class="FileUpload__dialog">
        <span>Drop files here or&nbsp;</span>
        <button class="btn btn--a" type="button" @click="openHtmlDialog">open dialog</button>
        <slot name="dialog-after" />
      </div>
    </slot>

    <template v-if="state !== STATES.EMPTY">
      <slot v-if="multiple" name="list">
        <ul class="FileUpload__list">
          <template v-for="(item, i) of items" :key="item.response ? item.response.id : i">
            <li v-if="item" class="FileUpload__item">
              <slot name="item" v-bind="{
                item, remove,
                url: item.response && getFileUrl(item.response.id, item.file.name),
              }">
                <a v-if="item.state === STATES.COMPLETE"
                   target="_blank"
                   :href="item.response ? getFileUrl(item.response.id, item.file.name) : ''" class="u-ellipsis"
                >
                  {{ item.file.name }}
                </a>
                <span v-else class="u-ellipsis">{{ item.file.name }}</span>
                <span>({{ flt.bytes(item.file.size) }})</span>
                <span v-if="item.state === STATES.PROGRESS && item.progress < 1">
                {{ (item.progress * 100).toFixed(1) + '%' }}
              </span>
                <button type="button" class="btn btn--clear FileUpload__remove"
                        @click="remove(item)">
                  <Icon name="close" />
                </button>
              </slot>
            </li>
          </template>
        </ul>
      </slot>

      <slot v-else name="only">
        <div v-if="items && items.file" class="FileUpload__item">
          <a v-if="items.state === STATES.COMPLETE"
             target="_blank"
             :href="getFileUrl(items.response.id, items.file.name)" class="u-ellipsis"
          >
            {{ items.file.name }}
          </a>
          <span v-else class="u-ellipsis">{{ items.file.name }}</span>
          <span>({{ flt.bytes(items.file.size) }})</span>
          <span v-if="items.state === STATES.PROGRESS && items.progress < 1">
            {{ (items.progress * 100).toFixed(1) + '%' }}
          </span>
          <button type="button" class="btn btn--clear FileUpload__remove"
                  @click="remove(items)">
            <Icon name="close" />
          </button>
        </div>
      </slot>
    </template>
  </div>
</template>
<script>
import { uploadFile, getFileUrl, getFileInfo } from 'api/files';
import { Enum, notifyError, notify, debounce, Format, objProp } from 'utils';
import Icon from '@ohmsd/v-ui/components/Icon';

export const STATES = new Enum('PROGRESS', 'EMPTY', 'COMPLETE', 'ERROR', 'EXISTS');
export { getFileUrl, getFileInfo };

export default {
  name: 'FileUpload',
  emits: ['error', 'statechange', 'input', 'update:value'],

  data: () => ({
    dragHover: false,
    state: STATES.EMPTY,
    items: [],
  }),

  methods: {
    _upload (item) {
      const upload = this.upload || uploadFile;

      objProp(item, '_uploading', upload(
        { file: item.file, category: this.category },
        this.onProgress.bind(this, item),
      ), 'W');

      item._uploading.then(
        res => {
          item.response = res.data;
          item.state = STATES.COMPLETE;
          this.setValue();
          item._uploading = null;
        },
        err => {
          item.error = err;
          this.state = STATES.ERROR;
          this.$emit('error', err);
          if (!this.$listeners.error)
            notifyError(err);
          item._uploading = null;
        },
      );

      return item._uploading;
    },

    async onDrop (e) {
      e.preventDefault();
      this.dragHover = false;

      const entries = await getFilesFromDataTransfer(e.dataTransfer);
      if (!this.multiple)
        entries.slice(0, 1);

      for (const entry of entries)
        this.push(entry.file);
    },

    hasValidSize (file) {
      const maxSize = +this.maxSize;
      if (maxSize < file.size) {
        this.notValid(`File "${ file.name }" file size `
          + `(${ Format.bytes(file.size) }) exceeds limit (${ Format.bytes(maxSize) })`);
        return false;
      }
      return true;
    },

    hasValidType (file) {
      if (!file.type) {
        const [, ext] = file.name.match(/\.([\d\w]+)$/) || [];
        if (ext && this.accept.split(',').find(mime => mime.split('/')[1] === ext))
          return true;
        this.notValid(`File "${ file.name }" has not acceptable type (${ file.type }).`);
        return false;
      }
      const accepts = this.accept.split(',');
      function isValid (accept, fileType) {
        const [fType, fExt] = fileType.split('/');
        const [aType, aExt] = accept.split('/');
        if (aType === '*' || aType === fType) {
          if (aExt === '*' || aExt === fExt)
            return true;
        }
        return false;
      }

      if (!accepts.some(accept => isValid(accept, file.type))) {
        this.notValid(`File "${ file.name }" has not acceptable type (${ file.type }).`);
        return false;
      }
      return true;
    },

    notValid (text) {
      return notify({
        type: 'warn',
        text,
      });
    },

    setState (state, items = this.items) {
      const nextState = (() => {
        if (state)
          return state;

        if (this.multiple) {
          if (!items.length)
            return STATES.EMPTY;

          for (const item of items) {
            if (item.state === STATES.ERROR)
              return STATES.ERROR;
            else if (item.state === STATES.PROGRESS)
              return STATES.PROGRESS;
          }
        } else
          return items.state || STATES.EMPTY;

        return STATES.COMPLETE;
      })();

      if (nextState === this.state)
        return;
      this.state = nextState;
      this.$nextTick(() =>
        this.$emit('statechange', nextState),
      );
    },

    onProgress: debounce((item, e) => {
      item.progress = Math.min(0.99, e.loaded / e.total);
    }, 60),

    reset () {
      this.setValue(this.multiple ? [] : null);
    },

    remove (item) {
      item._uploading?.cancel();
      if (!Array.isArray(this.items))
        return this.setValue([]);
      let items;
      let i;
      items = [...this.items];
      i = items.indexOf(item);
      items.splice(i, 1);
      this.setValue(items);
    },

    setValue (items = this.items) {
      const { name } = this;
      const value = Array.isArray(items) ? items.map(item => item.response) : items.response;

      this.items = items;

      const payload = name ? { target: { value, name } } : value;

      this.$emit('input', payload);
      this.$emit('update:value', payload);
      this.setState();
    },

    async push (file) {

      if (this.accept && !this.hasValidType(file)) return;
      if (this.maxSize && !this.hasValidSize(file)) return;
      if (this.validate
          && !(await this.validate(file).catch(err => {
            this.notValid(err);
            return false;
          }))
      )
        return;

      let { value, multiple } = this;
      const item = {
        progress: 0,
        state: STATES.PROGRESS,
        file,
        response: null,
        error: null,
      };

      if (multiple) {
        value = value || [];
        this.setValue([...this.items, item]);
      } else
        this.setValue(item);

      this._upload(item);
    },

    openHtmlDialog () {
      this.$refs.htmlInput.click();
    },

    onHtmlInputChange ({ target }) {
      const { files } = target;
      for (const file of files)
        this.push(file);
    },

    onDragOver (e) {
      e.dataTransfer.dropEffect = 'copy';
      this.dragHover = true;
      e.preventDefault();
    },


    getFileUrl,
  },

  watch: {
    value (value) {
      const { htmlInput } = this.$refs;
      if (!value || !value.length)
        htmlInput.value = null;
    },
  },

  computed: {
    htmlInputRequired () { return this.required
      && (!this.value || (Array.isArray(this.value) && !this.value.length));
    },
  },

  beforeUnmount () {
    if (Array.isArray(this.items))
      this.items.forEach(item =>
        item._uploading?.cancel(),
      );
    else
      this.items?._uploading?.cancel();
  },

  mounted () {
    this.$el.addEventListener('drop', this.onDrop, false);
    this.$el.addEventListener('dragover', this.onDragOver, false);

    if (this.value?.length) {
      this.items = this.value.map(item => ({
        progress: 0,
        state: STATES.EXISTS,
        file: null,
        response: item,
        error: null,
      }));
    }
  },

  unmounted () {
    this.$el.removeEventListener('drop', this.onDrop, false);
    this.$el.removeEventListener('dragover', this.onDragOver, false);
  },

  static: () => ({
    STATES,
  }),

  props: {
    value: [Object, Array],
    multiple: Boolean,
    upload: Function,
    name: String,
    accept: String,
    maxSize: [String, Number],
    required: Boolean,
    validate: Function,
    category: { type: String, default: 'attachment' },
  },

  components: { Icon },
};

function getFilesFromDataTransfer (dataTransfer) {
  let files = []
  if (dataTransfer.items && dataTransfer.items.length) {
    let items = []
    for (let i = 0; i < dataTransfer.items.length; i++) {
      let item = dataTransfer.items[i]
      if (item.getAsEntry) {
        item = item.getAsEntry() || item.getAsFile()
      } else if (item.webkitGetAsEntry) {
        item = item.webkitGetAsEntry() || item.getAsFile()
      } else {
        item = item.getAsFile()
      }
      if (item) {
        items.push(item)
      }
    }
    return new Promise((resolve, reject) => {
      let forEach = (i) => {
        let item = items[i]
        if (!item/* || (this.maximum > 0 && files.length >= this.maximum)*/) {
          return resolve(files)
        }
        getFileEntry(item).then(function (results) {
          files.push(...results)
          forEach(i + 1)
        })
      }
      forEach(0)
    })
  }
  if (dataTransfer.files.length) {
    for (let i = 0; i < dataTransfer.files.length; i++) {
      files.push(dataTransfer.files[i])
      if (this.maximum > 0 && files.length >= this.maximum) {
        break
      }
    }
    return Promise.resolve(files)
  }
  return Promise.resolve([])
}

function getFileEntry(entry, path = '') {
  return new Promise((resolve, reject) => {
    if (entry.isFile) {
      entry.file(function (file) {
        resolve([
          {
            size: file.size,
            name: path + file.name,
            type: file.type,
            file,
          }
        ])
      })
    } else if (entry.isDirectory && this.dropDirectory) {
      let files = []
      let dirReader = entry.createReader()
      let readEntries = () => {
        dirReader.readEntries((entries) => {
          let forEach = (i) => {
            if ((!entries[i] && i === 0) || (this.maximum > 0 && files.length >= this.maximum)) {
              return resolve(files)
            }
            if (!entries[i]) {
              return readEntries()
            }
            getFileEntry(entries[i], path + entry.name + '/').then((results) => {
              files.push(...results)
              forEach(i + 1)
            })
          }
          forEach(0)
        })
      }
      readEntries()
    } else {
      resolve([])
    }
  })
}
</script>

<style>
.FileUpload {
  position: relative;
  width: 100%;
  min-height: 94px;
  transition: all .2s;
  padding: var(--main-pad);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: #39434e0a;
  border: 1px solid var(--border-c);
  border-radius: 3px;

  &__item {
    height: 24px;
    line-height: 24px;

    display: flex;
    max-width: 100%;

    a, span {
      margin-right: .5em;
    }

    > * { max-width: 400px; }
  }

  &__remove {
    height: 24px;
    min-width: 24px;
  }

  &__list {
    width: 100%;
    margin-top: var(--main-pad);
  }

  &__htmlInput {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
  }

  &--drag-hover {
    border-color: var(--link-hover);
  }

  &__required {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    box-shadow: 0 0 0px 3px var(--link-50);
    border-radius: 3px;
    z-index: 0;
  }

  [type="file"]:focus ~ &__required {
    opacity: 1;
  }
}
</style>
